import { IMask } from 'react-imask';
import { isoBirthDateIsInRange, isoBirthDateIsUnder18, toIsoBirthDate } from '@/utils/date';
import { emailIsValid } from '@/utils/email';
import { mbiIsValid } from '@/utils/mbi';
import { FIRST_NAME_MAX_LENGTH, LAST_NAME_MAX_LENGTH, nameIsValid } from '@/utils/name';
import { PHONE_INPUT_MAX_DIGITS } from '@/utils/phone';
import { hasStartedWebreg } from '@/utils/webregUserIdGenerator';
import { WREG_STEP_NAME, type WregStep } from '@/components/webreg/wregSteps';

const MBI_MASK = '****-***-****';

type WregIntakeForm = {
  first: string;
  last: string;
  dob: string;
  gender: string;
  mbi: string;
  promo: string;
  email: string;
  phone: string;
  tcAccepted: boolean;
};

type WregIntakeTextField = Exclude<keyof WregIntakeForm, 'tcAccepted'>;

type IntakeErrorKey = WregIntakeTextField;

const INTAKE_NAME_FIELDS: IntakeErrorKey[] = ['first', 'last'];
const INTAKE_CONTACT_FIELDS: IntakeErrorKey[] = ['phone', 'email'];
const INTAKE_ABOUT_FIELDS: IntakeErrorKey[] = ['dob', 'gender'];
const INTAKE_MEDICARE_FIELDS: IntakeErrorKey[] = ['mbi'];
const INTAKE_PROMO_FIELDS: IntakeErrorKey[] = ['promo'];

const FIELD_NAME: Record<IntakeErrorKey, string> = {
  first: 'first_name',
  last: 'last_name',
  dob: 'dob',
  gender: 'gender',
  mbi: 'mbi',
  promo: 'promo_code',
  email: 'email',
  phone: 'phone',
};

const INTAKE_STEP_FIELDS: Partial<Record<WregStep, IntakeErrorKey[]>> = {
  intake: INTAKE_NAME_FIELDS,
  'intake-contact': INTAKE_CONTACT_FIELDS,
  'intake-about': INTAKE_ABOUT_FIELDS,
  'intake-mbi': INTAKE_MEDICARE_FIELDS,
};

const intakeFieldNames = (step: WregStep): string[] =>
  (INTAKE_STEP_FIELDS[step] ?? []).map((field) => FIELD_NAME[field]);

const intakeEventProperties = (step: WregStep): Record<string, unknown> => {
  const fields = intakeFieldNames(step);
  return fields.length
    ? { step_name: WREG_STEP_NAME[step], fields, is_retry: hasStartedWebreg() }
    : {};
};

const EMPTY_INTAKE: WregIntakeForm = {
  first: '',
  last: '',
  dob: '',
  gender: '',
  mbi: '',
  promo: '',
  email: '',
  phone: '',
  tcAccepted: false,
};

const formatMbi = (value: string): string => {
  const c = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 11);
  return [c.slice(0, 4), c.slice(4, 7), c.slice(7, 11)].filter(Boolean).join('-');
};

type MaskFlags = { input?: boolean; raw?: boolean; tail?: boolean };

type MaskPrepare = (value: string, masked: { value: string }, flags?: MaskFlags) => string;

type WregMaskOptions = {
  mask: string;
  prepare: MaskPrepare;
  definitions?: Record<string, RegExp>;
  eager?: boolean;
  placeholderChar?: string;
};

type TextAfterCaret = (value: string, masked: { value: string }, flags?: MaskFlags) => string;

const NOTHING_AFTER_CARET: TextAfterCaret = () => '';

// IMask hands prepare only the text before its insert. While it handles an input event (the only
// caller passing both input and raw) the field already shows the edit, so the rest of the field
// starts where IMask's head and insert end. Not at the caret: if IMask's saved selection is stale,
// its insert already holds text from after the caret, which would then be counted twice.
const textAfterCaret = (
  input: HTMLInputElement | null,
  value: string,
  masked: { value: string },
  flags?: MaskFlags,
): string =>
  input && flags?.input && flags.raw ? input.value.slice(masked.value.length + value.length) : '';

const digitsOf = (value: string): string => value.replace(/\D/g, '');

const FULL_DOB = /^(\d{4})-(\d{1,2})-(\d{1,2})$|^(\d{1,2})[/.-](\d{1,2})[/.-](\d{4})$/;

// IMask hands prepare the text before the caret as masked.value, and re-appends the text after it
// with flags.tail, which must pass through untouched.
const prepareDob = (
  value: string,
  masked: { value: string },
  flags?: MaskFlags,
  after = '',
): string => {
  if (flags?.tail) return value;
  const slot = digitsOf(masked.value).length;
  const full = FULL_DOB.exec(value.trim());
  if (full) {
    // Into a partly filled field a whole date would be cut to fit and read as a different one.
    if (slot > 0 || /\d/.test(after)) return '';
    const [m, d, y] = full[1] ? [full[2], full[3], full[1]] : [full[4], full[5], full[6]];
    return `${m.padStart(2, '0')}${d.padStart(2, '0')}${y}`;
  }
  const digits = digitsOf(value);
  // A digit left after the caret in the same block means a hole is being filled; a pad zero would
  // take the hole and push the typed digit out.
  const blockIsEmpty = !/\d/.test(after.replace(/^\//, '').split('/')[0]);
  const pad =
    blockIsEmpty && ((slot === 0 && /^[2-9]/.test(digits)) || (slot === 2 && /^[4-9]/.test(digits)));
  return pad ? `0${digits}` : digits;
};

// The backticks stop a deletion from pulling digits across a slash: the rest of that month, day or
// year closes up and the hole (shown as a space) sits at the end of the block, so the day never
// slides into the month nor the year into the day.
const dobMaskOptions = (after: TextAfterCaret = NOTHING_AFTER_CARET): WregMaskOptions => ({
  mask: '00/`00/`0000',
  placeholderChar: ' ',
  prepare: (value, masked, flags) =>
    prepareDob(value, masked, flags, after(value, masked, flags)),
});

// A US area code never starts with 1, so eleven digits led by a 1 carry the country code, and any
// other insert at the start led by a 1 is refused whole. So is an insert longer than the room left
// around the digits already in the field: IMask would keep what fits and drop the rest, which yields
// a real-looking wrong number.
const preparePhone = (
  value: string,
  masked: { value: string },
  flags?: MaskFlags,
  after = '',
): string => {
  if (flags?.tail) return value;
  const digits = digitsOf(value);
  const national = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  const before = digitsOf(masked.value).length;
  if (national.length > PHONE_INPUT_MAX_DIGITS - before - digitsOf(after).length) return '';
  return before === 0 && national.startsWith('1') ? '' : national;
};

// The first digit also admits 0, the leading digit of the Israeli tester numbers toE164 accepts.
const phoneMaskOptions = (after: TextAfterCaret = NOTHING_AFTER_CARET): WregMaskOptions => ({
  mask: '(#00) 000-0000',
  definitions: { '#': /[02-9]/ },
  eager: true,
  prepare: (value, masked, flags) =>
    preparePhone(value, masked, flags, after(value, masked, flags)),
});

const formatPhone = IMask.createPipe(
  phoneMaskOptions(),
  IMask.PIPE_TYPE.UNMASKED,
  IMask.PIPE_TYPE.MASKED,
);

const dobIsOutOfRange = (dob: string): boolean => {
  const iso = toIsoBirthDate(dob);
  return iso !== '' && !isoBirthDateIsInRange(iso);
};

const dobIsImpossible = (dob: string): boolean =>
  dob.replace(/\D/g, '').length === 8 && toIsoBirthDate(dob) === '';

const dobIsUnlikely = (dob: string): boolean => {
  const iso = toIsoBirthDate(dob);
  return isoBirthDateIsInRange(iso) && isoBirthDateIsUnder18(iso);
};

const dobIsComplete = (f: WregIntakeForm): boolean => {
  const iso = toIsoBirthDate(f.dob);
  return iso !== '' && isoBirthDateIsInRange(iso);
};

const intakeNameIsComplete = (f: WregIntakeForm): boolean =>
  nameIsValid(f.first, FIRST_NAME_MAX_LENGTH) && nameIsValid(f.last, LAST_NAME_MAX_LENGTH);

const intakeContactIsComplete = (f: WregIntakeForm): boolean =>
  Boolean(f.phone.length === 10 && emailIsValid(f.email));

const intakeAboutIsComplete = (f: WregIntakeForm): boolean =>
  Boolean(dobIsComplete(f) && f.gender);

const intakeMedicareIsComplete = (f: WregIntakeForm, invite = false): boolean =>
  invite ? f.promo.trim() !== '' : mbiIsValid(f.mbi);

const intakeIsComplete = (f: WregIntakeForm, invite = false): boolean =>
  intakeNameIsComplete(f) &&
  intakeContactIsComplete(f) &&
  intakeAboutIsComplete(f) &&
  intakeMedicareIsComplete(f, invite);

export {
  EMPTY_INTAKE,
  FIELD_NAME,
  INTAKE_ABOUT_FIELDS,
  INTAKE_CONTACT_FIELDS,
  INTAKE_MEDICARE_FIELDS,
  INTAKE_NAME_FIELDS,
  INTAKE_PROMO_FIELDS,
  MBI_MASK,
  dobIsComplete,
  dobIsImpossible,
  dobIsOutOfRange,
  dobIsUnlikely,
  dobMaskOptions,
  formatMbi,
  formatPhone,
  intakeAboutIsComplete,
  intakeContactIsComplete,
  intakeEventProperties,
  intakeFieldNames,
  intakeIsComplete,
  intakeMedicareIsComplete,
  intakeNameIsComplete,
  phoneMaskOptions,
  prepareDob,
  preparePhone,
  textAfterCaret,
};
export type {
  IntakeErrorKey,
  TextAfterCaret,
  WregIntakeForm,
  WregIntakeTextField,
  WregMaskOptions,
};
